import React, { useEffect, useRef, useState, FC, useCallback } from "react";
import {
  Application,
  Container,
  Texture,
  Assets,
  Point as PixiPoint,
  Text,
  TextStyle,
} from "pixi.js";
import { Viewport } from "pixi-viewport";
import chalk from "chalk";
import { useLocation, useParams } from "wouter";
import { FilterConnections } from "../../components/FilterConnections";
import { Artwork, IArtworkSprite, Cluster, NodeData } from "../../interfaces";
import {
  calculateCentroidForNode,
  calculateOptimalZoomAndCenter,
  generateConvexHull,
} from "../../utils/math";
import { gsap } from "gsap";
import { PixiPlugin } from "gsap/PixiPlugin";
import ZoomControl, { ZoomControlSize } from "../../components/ZoomControl";
import {
  addArtworkSpriteEvents,
  createArtworkSprite,
  drawTaperedCurveWithOpacity,
} from "../../utils/pixi";
import { ClusterSimulation } from "../../simulation/ClusterSimulation";
import { useActiveClusterCoverage } from "../../hooks/useActiveClusterCoverage";

import Loading from "../../components/Loading";
import RoundButton from "../../components/buttons/RoundButton";
import { isDebugMode } from "../../config";
import ArtworkDetailView, { ArtworkDetailViewRef } from "../ArtworkDetail";
import { useStore } from "../../store/store";
import _ from "lodash";
gsap.registerPlugin(PixiPlugin);

// --------------------
// Constants
// --------------------
const DOUBLE_CLICK_THRESHOLD = 300; // 300 ms
const MIN_ZOOM = 0.01;
const MAX_ZOOM = 8;
const DEFAULT_CATEGORY = "Kies onderwerp";

/**
 * Extracts the file extension from a given URL.
 * Returns the extension without the dot, e.g., "png", "jpeg".
 * If no extension is found, returns an empty string.
 * @param url The URL string to extract the extension from.
 */
const getFileExtension = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const lastDotIndex = pathname.lastIndexOf(".");
    if (lastDotIndex === -1) return "";
    return pathname.substring(lastDotIndex + 1).toLowerCase();
  } catch (error) {
    console.error("Invalid URL:", url);
    return "";
  }
};

const loadJSON = async (url: string) => {
  const response = await fetch(url);
  return await response.json();
};

// --------------------
// Main Component
// --------------------
const ConnectionView: FC = () => {
  // --------------------
  // Refs
  // --------------------
  const [, navigate] = useLocation();
  const params = useParams();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pixiAppRef = useRef<Application | null>(null);
  const viewportRef = useRef<Viewport | null>(null);
  const hasFetchedData = useRef(false);
  const simulationRef = useRef<ClusterSimulation | null>(null);
  let lastClickTimeRef = useRef<number>(0);
  const artworkContainerRef = useRef<Container>(new Container());
  artworkContainerRef.current.zIndex = 11;
  const clusterLinkContainerRef = useRef<Container>(new Container());
  clusterLinkContainerRef.current.zIndex = 10;
  const debugLayerRef = useRef<Container>(new Container());

  // --------------------
  // Store Hooks
  // --------------------
  const { combinedTextures, setCombinedTextures } =
    useStore();

  // --------------------
  // State Variables
  // --------------------
  const [texturesLoaded, setTexturesLoaded] = useState<boolean>(false);
  const [spritesSetup, setSpritesSetup] = useState<boolean>(false);
  const [isDataFetched, setIsDataFetched] = useState<boolean>(false);
  const [centerArtwork, setCenterArtwork] = useState<Artwork | null>(null);
  const artworkRef = useRef<ArtworkDetailViewRef>(null);
  const [uuid, setUuid] = useState<string | null>(null);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [artworkSprites, setArtworkSprites] = useState<IArtworkSprite[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [isRendered, setIsRendered] = useState<boolean>(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>();

  const [dimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [worldDimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: window.innerWidth * 2,
    height: window.innerHeight * 2,
  });
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const [isAnimating] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // --------------------
  // Computed Values
  // --------------------
  const hasData = artworks.length !== 0;
  const centerArtworkUuid = params.uuid;

  // --------------------
  // Effects
  // --------------------

  // First sequence: Fetch data
  useEffect(() => {
    if (!hasFetchedData.current) {
      console.group("Sequence");
      console.info(chalk.cyan("[SEQ1]") + "Fetching data");
      fetchArtworkConnections();
      hasFetchedData.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Second sequence: Load textures after data is fetched
  useEffect(() => {
    if (hasData && isDataFetched && !texturesLoaded && !simulationRef.current) {
      console.info(chalk.cyan("[SEQ2]") + "Loading textures");
      loadArtworkTextures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData, isDataFetched, texturesLoaded]);

  // Third sequence: Setup sprites after textures are loaded
  useEffect(() => {
    if (texturesLoaded) {
      console.info(chalk.cyan("[SEQ3]") + "Setting up sprites");
      setupSprites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texturesLoaded]);

  // Fourth sequence: Run simulation after sprites are set up
  useEffect(() => {
    if (spritesSetup) {
      console.info(chalk.cyan("[SEQ4]") + "Running simulation");
      runSimulation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spritesSetup]);

  // Fifth sequence: Render PIXI after simulation finishes
  useEffect(() => {
    if (
      clusters.length &&
      texturesLoaded &&
      spritesSetup &&
      !pixiAppRef.current
    ) {
      console.info(chalk.cyan("[SEQ5]") + "Render pixi");
      console.groupEnd();
      renderPixi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusters, texturesLoaded, spritesSetup, pixiAppRef.current]);

  // Handle window resize to adjust viewport
  useEffect(() => {
    const handleResize = () => {
      if (viewportRef.current && pixiAppRef.current) {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;
        pixiAppRef.current.renderer.resize(newWidth, newHeight);
        viewportRef.current.resize(newWidth, newHeight, newWidth, newHeight);
        adjustViewport();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------
  // Reset on Unmount
  // --------------------
  useEffect(() => {
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs on unmount

  const cleanup = useCallback(() => {
    // Stop and destroy the current simulation
    if (simulationRef.current) {
      simulationRef.current.instance.stop();
      simulationRef.current = null;
    }

    // Destroy PIXI Application
    if (pixiAppRef.current) {
      pixiAppRef.current.destroy(true, {
        children: true,
        texture: true,
      });
      pixiAppRef.current = null;
    }
    // Destroy Viewport
    if (viewportRef.current) {
      viewportRef.current.destroy();
      viewportRef.current = null;
    }
  }, []);

  // --------------------
  // Data Fetching and Processing
  // --------------------
  const fetchArtworkConnections = async () => {
    setIsDataFetched(true);
    setLoadingProgress(10); // Start with 10%
    try {
      const response = await fetch(
        `https://admin.collectieconnectie.nl/api/v1/artworks/${centerArtworkUuid}/connections`,
      );
      const result = await response.json();
      setCenterArtwork(result.selected);
      transformArtworkData(result);
      setLoadingProgress(30); // Data fetched successfully
    } catch (error) {
      console.error("Error fetching artworks data:", error);
      setArtworks([]);
      setIsRendered(true);
      setLoadingProgress(100); // Loading complete even on error
      setIsLoading(false);
    }
  };

  const transformArtworkData = (response: any) => {
    let allConnections = response.connections;
    if (
      allConnections.length !== 0 &&
      allConnections.every((artwork: any) => artwork)
    ) {
      allConnections = allConnections.concat(response.selected);
      const mappedArtworks: Artwork[] = allConnections.map((artwork: any) => {
        return {
          uuid: artwork.uuid,
          name: artwork.title,
          dateTime: `${artwork.details.start_year}-01-01`,
          tags: artwork.tags,
          imageUrl: artwork.medium_image_url,
        };
      });
      setArtworks(mappedArtworks);
      setLoadingProgress(50); // Data processed
    } else {
      setArtworks([]);
      setIsRendered(true);
      setLoadingProgress(100); // Loading complete even if no data
      setIsLoading(false);
    }
  };

  const { activeCluster } = useActiveClusterCoverage({
    viewportRef,
    clusters,
    dimensions,
    onActiveClusterChange: (cluster) => {
      if (cluster) {
        console.debug("Active cluster:", cluster);
      }
    },
  });

  // --------------------
  // Force Simulation
  // --------------------

  const runSimulation = useCallback(async () => {
    if (
      !centerArtwork ||
      (!artworks.length && !artworkSprites.length && !simulationRef.current)
    )
      return;
    const simulation = new ClusterSimulation(
      centerArtwork,
      artworks,
      artworkSprites,
      worldDimensions,
    );
    simulation.onSimulationEnd = async (root) => {
      const allCategories = new Set<string>([DEFAULT_CATEGORY]);
      root.children!.forEach(async (_cluster: any) => {
        const clusterName = (_cluster.data.data as NodeData).name || "";
        const categories = clusterName.split(",").map((name) => name.trim());
        categories.forEach((category) => allCategories.add(category));
        const id = btoa(clusterName);
        const clusterSize = _cluster.children?.length || 0;
        const centroid = calculateCentroidForNode(_cluster);
        setClusters((prevClusters) => [
          ...prevClusters,
          {
            id,
            name: (_cluster.data.data as NodeData).name || "",
            categories,
            parent: _cluster.parent,
            children: _cluster.children,
            centroid: new PixiPoint(centroid.x, centroid.y),
            size: clusterSize,
            angle: 0,
          },
        ]);
      });
      setLoadingProgress(60);
      simulationRef.current = simulation;
      setCategories([...allCategories]);
      setLoadingProgress(90);
    };
    simulation.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerArtwork, artworks, artworkSprites, simulationRef.current]);

  // --------------------
  // PIXI.js Integration
  // --------------------
  const setupSprites = useCallback(async () => {
    if (!combinedTextures || !centerArtwork) return;

    try {
      const artworkSprites = await Promise.all(
        artworks.map(async (artwork) => {
          const extension = getFileExtension(artwork.imageUrl) || "jpg";
          const key = `${artwork.uuid}.${extension}`;

          let texture;
          if (artwork.uuid === centerArtworkUuid) {
            texture = await Assets.load(artwork.imageUrl);
            texture.label = artwork.uuid;
          } else {
            texture = combinedTextures[key] || Texture.WHITE;
          }

          return createArtworkSprite(
            artwork.uuid,
            texture,
            artwork.uuid === centerArtworkUuid,
          );
        }),
      );

      if (artworkSprites.length === 0) {
        console.warn("No valid artworks for sprites found.");
        return;
      }

      setArtworkSprites(artworkSprites);
      setSpritesSetup(true);
    } catch (error) {
      console.error("Error setting up sprites:", error);
      setSpritesSetup(false);
    }
  }, [combinedTextures, artworks, centerArtwork, centerArtworkUuid]);

  const loadArtworkTextures = useCallback(async () => {
    try {
      // Start loading: set initial progress to 10%
      setLoadingProgress(10);

      // Check if combinedTextures are already loaded
      if (!_.isEmpty(combinedTextures)) {
        console.info(
          "Combined textures already loaded. Skipping texture loading.",
        );
        setTexturesLoaded(true);
        setLoadingProgress(90);
        return;
      }

      const baseUrl =
        "https://museumarnhem.ams3.digitaloceanspaces.com/spritesheet/";
      // Load the main spritesheet JSON
      const mainSpritesheetUrl = `${baseUrl}spritesheet.json`; // Update with your actual URL
      const mainData = await loadJSON(mainSpritesheetUrl);
      setLoadingProgress(20); // Update progress after main JSON is loaded

      if (!mainData.meta || !mainData.meta.related_sheets) {
        console.error("No related_sheets found in main JSON.");
        return;
      }

      const relatedSheets: string[] = mainData.meta.related_sheets;
      const totalSheets = relatedSheets.length;

      const sheets: any[] = [];

      // Load each related spritesheet sequentially
      for (const sheetUrl of relatedSheets) {
        // Construct the full URL for the related spritesheet
        // Update with your actual base URL
        const fullSheetUrl = `${baseUrl}${sheetUrl}`;

        // Load the spritesheet using PixiJS Assets
        const spritesheet = await Assets.load(fullSheetUrl);
        sheets.push(spritesheet);

        const progressIncrement = 70 / totalSheets;
        setLoadingProgress((prev) =>
          Math.round(Math.min(prev + progressIncrement, 90)),
        );
      }

      // Combine all textures from the loaded spritesheets
      const allTextures: Record<string, Texture> = {};
      for (const sheet of sheets) {
        for (const [name, tex] of Object.entries(sheet.textures)) {
          // @ts-ignore
          allTextures[name] = tex;
        }
      }
      setCombinedTextures(allTextures); // Update the store
      setLoadingProgress(90); // Update progress after combining textures
      setTexturesLoaded(true);
    } catch (error) {
      console.error("Error loading spritesheet:", error);
      // Optionally, you can set progress to 100% and handle the error state
      setLoadingProgress(100);
      setIsLoading(false);
    }
  }, [combinedTextures, setCombinedTextures]);

  const renderPixi = useCallback(async () => {
    if (
      !centerArtworkUuid ||
      !simulationRef.current ||
      !artworkContainerRef.current ||
      !clusterLinkContainerRef.current ||
      !canvasRef.current ||
      !clusters.length
    ) {
      return;
    }

    const simulation = simulationRef.current;
    const pixiApp = new Application();
    PixiPlugin.registerPIXI(pixiApp);
    await pixiApp.init({
      canvas: canvasRef.current,
      width: dimensions.width,
      height: dimensions.height,
      resizeTo: window,
      resolution: window.devicePixelRatio || 1,
      antialias: true,
      autoDensity: true,
      useBackBuffer: true,
      background: "#1A1919",
    });

    pixiAppRef.current = pixiApp;

    // Initialize Viewport
    if (viewportRef.current) return;
    const viewport = new Viewport({
      screenWidth: dimensions.width,
      screenHeight: dimensions.height,
      worldWidth: worldDimensions.width,
      worldHeight: worldDimensions.height,
      events: pixiApp.renderer.events,
    });
    viewport.on("drag-start", () => {
      pixiApp.canvas.style.cursor = "grabbing";
    });
    viewport.on("drag-end", () => {
      pixiApp.canvas.style.cursor = "grab";
    });

    viewportRef.current = viewport;
    pixiApp.stage.addChild(viewport);

    viewport
      .drag()
      .pinch()
      .wheel()
      .decelerate()
      .clampZoom({
        minWidth: (worldDimensions.width / 2) * MIN_ZOOM,
        maxWidth: (worldDimensions.width / 2) * MAX_ZOOM,
        minHeight: (worldDimensions.height / 2) * MIN_ZOOM,
        maxHeight: (worldDimensions.height / 2) * MAX_ZOOM,
        minScale: MIN_ZOOM,
        maxScale: MAX_ZOOM,
      });

    viewport.addChild(artworkContainerRef.current);
    viewport.addChild(clusterLinkContainerRef.current);
    if (isDebugMode) {
      viewport.addChild(debugLayerRef.current);
    }

    // Render Artwork Sprites
    artworkSprites.forEach((sprite, index) => {
      const node = simulation.nodes[index];
      if (!node) return;

      addArtworkSpriteEvents(sprite, node, {
        onPointerTap: () => onArtworkTap(sprite, node),
        onSpriteOver: () => onArtworkMouseOver(sprite),
        onSpriteOut: () => onArtworkMouseOut(sprite),
      });
      sprite.x = node.x;
      sprite.y = node.y;
      artworkContainerRef.current.addChild(sprite);
    });

    setLoadingProgress(100);
    setIsRendered(true);
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    centerArtworkUuid,
    clusters.length,
    canvasRef.current,
    simulationRef.current,
    artworkContainerRef.current,
    clusterLinkContainerRef.current,
    artworkSprites, // Ensure artworkSprites is included as a dependency
    dimensions,
    worldDimensions,
    isDebugMode,
  ]);

  useEffect(() => {
    if (
      simulationRef.current &&
      viewportRef.current &&
      loadingProgress === 100 &&
      isRendered &&
      !isLoading
    ) {
      viewportRef.current.moveCenter(
        simulationRef.current?.centerNode.x,
        simulationRef.current?.centerNode.y,
      );
      animateZoom(viewportRef.current, MIN_ZOOM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    simulationRef.current,
    viewportRef.current,
    loadingProgress,
    isLoading,
    isRendered,
  ]);

  useEffect(() => {
    if (!pixiAppRef.current) return;

    const app = pixiAppRef.current;
    const stage = app.stage;

    const checkIfBackgroundTextExist = stage.children.find(
      (child) => child.label === "backgroundText",
    );
    if (!checkIfBackgroundTextExist) {
      const textStyle = new TextStyle({
        fontFamily: "ABC Favorit",
        fontSize: Math.min(dimensions.width, dimensions.height) * 0.1, // Scale font size
        fill: "#FFFFFF",
        align: "left",
        breakWords: true,
        wordWrapWidth: dimensions.width * 0.3,
      });

      const text = new Text({
        text: "",
        style: textStyle,
      });
      text.label = "backgroundText";
      text.zIndex = 1;
      text.blendMode = "overlay";

      // Center the text
      text.anchor.set(0.5);
      text.x = dimensions.width / 2;
      text.y = dimensions.height / 2;

      // Add text to the stage
      stage.addChild(text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixiAppRef.current]);

  useEffect(() => {
    if (!pixiAppRef.current) return;
    const app = pixiAppRef.current;
    const stage = app.stage;

    const backgroundText = stage.children.find(
      (child) => child.label === "backgroundText" && child instanceof Text,
    );
    if (isRendered && activeCluster) {
      if (backgroundText) {
        const string = activeCluster.categories.join("\n");
        // @ts-ignore
        backgroundText.text = string;
        gsap.to(backgroundText, {
          pixi: { alpha: 0.2 },
          duration: 0.3,
          overwrite: true,
        });
      }
    } else {
      if (backgroundText) {
        gsap.to(backgroundText, {
          pixi: { alpha: 0 },
          duration: 0.3,
          overwrite: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixiAppRef.current, isRendered, activeCluster]);

  const drawLinks = useCallback(() => {
    if (
      !texturesLoaded ||
      !isRendered ||
      !clusters.length ||
      !viewportRef.current ||
      !clusterLinkContainerRef.current ||
      !simulationRef.current ||
      !artworkContainerRef.current.children.length
    ) {
      return;
    }

    const imagesLayer = artworkContainerRef.current;
    const viewport = viewportRef.current;
    const clusterLinkContainer = clusterLinkContainerRef.current;
    clusterLinkContainer.removeChildren();

    // Pre-calculate image lookup map for faster access
    const imagesByLabel = new Map();
    imagesLayer.children.forEach((child) => {
      imagesByLabel.set(child.label, child);
    });

    // Reusable objects to avoid garbage collection
    const tempPoint = new PixiPoint(0, 0);
    const start = { x: 0, y: 0 };
    const end = { x: 0, y: 0 };

    const centerNode = simulationRef!.current!.centerNode;
    start.x = centerNode.x;
    start.y = centerNode.y;

    // Process all clusters in batch
    const clusterContainers = clusters.map((cluster) => {
      // Create and configure cluster container
      const clusterContainer = new Container();
      clusterContainer.zIndex = 10;
      clusterContainer.label = cluster.id;
      clusterContainer.eventMode = "static";
      clusterContainer.alpha = 0.15;
      clusterLinkContainer.addChild(clusterContainer);

      // Calculate cluster properties once
      const centroid = calculateCentroidForNode(cluster);
      tempPoint.set(centroid.x, centroid.y);
      cluster.centroid = tempPoint.clone();

      // Calculate boundary only if needed
      if (cluster.children?.length) {
        const points = cluster.children.map((d) => ({ x: d.x!, y: d.y! }));
        cluster.boundary = generateConvexHull(points);
      }

      if (cluster.children?.length && simulationRef.current!.centerNode) {
        cluster.children.forEach((child) => {
          if (!child.x || !child.y) return;

          end.x = child.x;
          end.y = child.y;

          const control = tempPoint.clone();

          drawTaperedCurveWithOpacity(
            clusterContainer,
            start,
            control,
            end,
            8,
            1,
            0xffffff,
            cluster.size < 10 ? 0.77 : 0.33,
          );
        });
      }

      // Optimize event handlers using closure for better performance
      const handlePointerOver = () => {
        gsap.to(clusterContainer, {
          pixi: { alpha: 1.0 },
          duration: 0.3,
          overwrite: true, // Prevent animation queuing
        });
      };

      const handlePointerOut = () => {
        gsap.to(clusterContainer, {
          pixi: { alpha: 0.15 },
          duration: 0.3,
          overwrite: true,
        });
      };

      const handleClick: () => void = () => {
        if (!viewport) return;

        viewport.moveCenter(cluster.centroid!.x, cluster.centroid!.y);

        const { zoom } = calculateOptimalZoomAndCenter(cluster, viewport);
        animateZoom(viewport, zoom);

        setZoomLevel(zoom);
      };

      // Attach optimized event listeners
      clusterContainer
        .on("pointerover", handlePointerOver)
        .on("pointerout", handlePointerOut)
        .on("pointertap", handleClick);

      return clusterContainer;
    });

    // Cleanup function to properly remove containers and event listeners
    return () => {
      clusterContainers.forEach((container) => {
        container.removeAllListeners();
        container.destroy({ children: true });
      });
      clusterLinkContainer.destroy({ children: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    texturesLoaded,
    isRendered,
    clusters.length,
    centerArtworkUuid,
    artworkContainerRef.current.children.length,
    viewportRef.current,
    simulationRef.current,
    clusterLinkContainerRef.current,
  ]);

  /**
   * Adjusts the viewport to center on the center artwork and set the appropriate zoom level
   * to fit all nodes within the screen.
   */
  const adjustViewport = useCallback(() => {
    if (!viewportRef.current || !artworkContainerRef.current) return;
    const viewport = viewportRef.current;
    // Calculate bounding box
    const bounds = artworkContainerRef.current.getBounds();

    const layoutWidth = bounds.maxX - bounds.minX;
    const layoutHeight = bounds.maxY - bounds.minY;

    // Calculate scale factors to fit the layout within the viewport
    const scaleX = dimensions.width / layoutWidth;
    const scaleY = dimensions.height / layoutHeight;
    const zoom = Math.min(scaleX, scaleY) * 0.9; // Slightly reduce zoom to ensure padding

    // Calculate center of the layout
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    // Update the zoom level state
    setZoomLevel(zoom);

    // Set the viewport to center on the layout's center and apply the calculated zoom
    viewport.moveCenter(centerX, centerY);
    viewport.setZoom(zoom, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewportRef.current,
    dimensions.width,
    dimensions.height,
    artworkContainerRef.current,
  ]);

  // --------------------
  // Event Handlers
  // --------------------
  const onArtworkTap = (sprite: IArtworkSprite, node: any) => {
    const currentTime = Date.now();
    if (currentTime - lastClickTimeRef.current < DOUBLE_CLICK_THRESHOLD) {
      if (isAnimating) {
        lastClickTimeRef.current = currentTime;
        return;
      }
    }
    if (sprite.label !== centerArtworkUuid) {
      setUuid(sprite.label);
    }
    lastClickTimeRef.current = currentTime;
  };

  const onArtworkMouseOver = (sprite: IArtworkSprite) => {
    if (isAnimating) return;
    sprite.scale.set(sprite.scale.x * 1.1, sprite.scale.y * 1.1); // Example hover effect
  };

  const onArtworkMouseOut = (sprite: IArtworkSprite) => {
    if (isAnimating) return;
    sprite.scale.set(sprite.scale.x / 1.1, sprite.scale.y / 1.1); // Revert hover effect
  };

  /**
   * Animates the zoom level of the viewport from the current zoom to the target zoom.
   *
   * @param {Viewport | null} viewport - The PIXI-Viewport instance or null.
   * @param {number} targetZoom - The desired zoom level.
   * @param {number} duration - Duration of the animation in seconds.
   */
  const animateZoom = (
    viewport: Viewport | null,
    targetZoom: number,
    duration: number = 0.5,
  ) => {
    if (!viewport) return; // Early exit if viewport is null

    // Kill any existing zoom animations to prevent conflicts
    gsap.killTweensOf(zoomRef.current);

    // Create a tween that interpolates the zoom level
    gsap.to(zoomRef.current, {
      zoom: targetZoom,
      duration: duration,
      ease: "power2.out",
      onUpdate: () => {
        viewport.setZoom(zoomRef.current.zoom, true);
      },
      onComplete: () => {
        // Optionally, perform any actions after the zoom completes
      },
    });
  };

  // Create a ref to store the current zoom value for GSAP to animate
  const zoomRef = useRef({ zoom: zoomLevel });

  useEffect(() => {
    zoomRef.current.zoom = zoomLevel;
  }, [zoomLevel]);

  const handleZoomLevelChange = () => {
    // Implement actions based on zoom level
    // For example, hide/show certain layers or elements
    console.log("Zoom level changed to:", zoomLevel);
  };

  useEffect(() => {
    if (!viewportRef.current) return;

    const viewport = viewportRef.current;

    const onZoomed = () => {
      const currentZoom = viewport.scale.x;
      setZoomLevel(currentZoom);
    };

    viewport.on("zoomed-end", onZoomed);

    // Cleanup the event listener on component unmount
    return () => {
      viewport.off("zoomed-end", onZoomed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportRef.current]);

  const handleZoomChange = (value: number) => {
    console.log("handleZoomChange", value);
    const clampedZoom = Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);

    setZoomLevel(clampedZoom);

    if (viewportRef.current) {
      // Type narrowing
      animateZoom(viewportRef.current, clampedZoom);
    }

    handleZoomLevelChange();
  };

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoomLevel + 0.5, MAX_ZOOM); // Prevent exceeding max zoom
    setZoomLevel(newZoom);

    if (viewportRef.current) {
      // Type narrowing
      animateZoom(viewportRef.current, newZoom);
    }

    handleZoomLevelChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomLevel, viewportRef.current]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel - 0.5, MIN_ZOOM); // Prevent going below min zoom
    setZoomLevel(newZoom);

    if (viewportRef.current) {
      // Type narrowing
      animateZoom(viewportRef.current, newZoom);
    }
    handleZoomLevelChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomLevel, viewportRef.current]);

  const navigateBack = useCallback(() => {
    if (activeCluster && viewportRef.current) {
      viewportRef.current.moveCenter(
        simulationRef.current?.centerNode.x,
        simulationRef.current?.centerNode.y,
      );
      animateZoom(viewportRef.current, 0.1);
      return;
    }
    navigate(`/collection/${centerArtworkUuid}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeCluster,
    viewportRef.current,
    simulationRef.current,
    centerArtworkUuid,
  ]);

  const handleCategorySelect = useCallback(
    (category: string) => {
      if (!simulationRef.current && !viewportRef.current) return;
      const simulation = simulationRef.current;
      const clusterLinkContainer = clusterLinkContainerRef.current;
      if (simulationRef.current) {
        if (category !== DEFAULT_CATEGORY) {
          clusterLinkContainer.visible = false;
          simulation!.filterNodesByCategories([category]);
        } else {
          if (selectedCategory === DEFAULT_CATEGORY) {
            simulation!.resetSimulation({
              onTick: () => {},
              onTickEnd: () => {
                drawLinks();
                clusterLinkContainer.visible = true;
              },
            });
          }
        }
        setSelectedCategory(category);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      simulationRef.current,
      viewportRef.current,
      clusterLinkContainerRef.current,
      selectedCategory,
    ],
  );

  // --------------------
  // Render JSX
  // --------------------
  return (
    <>
      <div className="z-20 h-full w-full absolute top-0 left-0 bg-primary select-none overscroll-none">
        <canvas ref={canvasRef} className="h-full w-full top-0 left-0" />
        <div className="h-full w-full top-0 left-0 z-30">
          {/* Total Clusters Display */}
          {isDebugMode && (
            <button className="fixed bottom-4 left-4 z-40 bg-white bg-opacity-80 text-gray-800 px-4 py-2 rounded shadow-md">
              <span className="font-semibold">Clusters:</span>{" "}
              {clusters!.length}
            </button>
          )}

          {/* Loading */}
          <Loading
            isLoading={isLoading}
            progress={loadingProgress}
            message="Verbindingen laden..."
          />

          {/* No Data Message */}
          {isRendered && artworks.length === 0 && (
            <div className="flex items-center justify-center h-screen">
              <div className="text-white text-3xl text-center">
                No connections
              </div>
            </div>
          )}

          {/* Filter Connections Component */}
          {!isLoading && (
            <FilterConnections
              categories={categories}
              onCategorySelect={handleCategorySelect}
            />
          )}

          {/* Zoom Control Component */}
          {!isLoading && (
            <ZoomControl
              size={ZoomControlSize.SMALL}
              zoomLevel={zoomLevel}
              onZoomChange={handleZoomChange}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              bottomClass={"bottom-20"}
            />
          )}

          {/* Back Button */}
          {!isLoading && (
            <div className="top-8 left-4 fixed z-20">
              <RoundButton
                onClick={navigateBack}
                icon={activeCluster ? "d-network-platform" : "d-arrow-left"}
              />
            </div>
          )}
        </div>
      </div>
      {uuid && (
        <ArtworkDetailView
          ref={artworkRef}
          uuid={uuid}
          onClose={() => {
            setUuid(null);
          }}
        />
      )}
    </>
  );
};

// --------------------
// Export Component
// --------------------
export default ConnectionView;
